/**
 * Mapping from Figma SVG path IDs to US state codes
 *
 * This file maps the auto-generated path IDs from the Figma export
 * to standard 2-letter state codes for integration with the Neon database.
 *
 * To build this mapping:
 * 1. Run the app and click each state
 * 2. Note the path ID shown in the alert
 * 3. Visually identify which state was clicked
 * 4. Add the mapping below
 *
 * Example process:
 * - Click on Texas → alert shows "p2fa1ab80"
 * - Add: p2fa1ab80: "TX"
 */

export const pathToStateCode: Record<string, string> = {
  // Federal and DC
  p1af8bcf0: "FEDERAL",  // Federal FOIA
  p156df200: "DC",       // District of Columbia

  // 50 US States (completed mapping)
  p29c39000: "TX",       // Texas
  p1f82cd70: "LA",       // Louisiana
  p3d733480: "MS",       // Mississippi
  p3fc06ff0: "AL",       // Alabama
  p351fc3f0: "GA",       // Georgia
  p11dea040: "FL",       // Florida
  p20543900: "SC",       // South Carolina
  p33d26100: "NC",       // North Carolina
  p5e96f00: "VA",        // Virginia
  p341cc200: "MD",       // Maryland (main section)
  pc095800: "PA",        // Pennsylvania
  p385bb900: "NY",       // New York
  p3958a7d2: "DE",       // Delaware
  p37c2900: "WV",        // West Virginia
  p3c307b80: "CT",       // Connecticut
  p22f5dc40: "RI",       // Rhode Island
  p1919f4c0: "MA",       // Massachusetts
  p3bede600: "VT",       // Vermont
  p10333180: "NH",       // New Hampshire
  p1a8c80: "NJ",         // New Jersey
  p2fd1000: "ME",        // Maine
  p32956700: "MI",       // Michigan
  pb0bda00: "WI",        // Wisconsin
  p2232f500: "MN",       // Minnesota
  p29d20480: "IL",       // Illinois
  p2f758200: "IN",       // Indiana
  p2a9b2b80: "OH",       // Ohio
  p1e308800: "KY",       // Kentucky
  p48c120: "TN",         // Tennessee
  p4758120: "IA",        // Iowa
  p26768a80: "MO",       // Missouri
  p3038db00: "AR",       // Arkansas
  p264d1000: "OK",       // Oklahoma
  p26d2def0: "KS",       // Kansas
  p12fb9380: "NE",       // Nebraska
  p2e73f0: "SD",         // South Dakota
  p276cce00: "ND",       // North Dakota
  p2fa1ab80: "MT",       // Montana
  p292cf900: "WY",       // Wyoming
  p20826b00: "CO",       // Colorado
  p13248800: "NM",       // New Mexico
  p1aa42a00: "AZ",       // Arizona
  p1757c900: "UT",       // Utah
  p2cd1e800: "ID",       // Idaho
  pca11d00: "WA",        // Washington
  p2b85300: "OR",        // Oregon
  p2989af00: "NV",       // Nevada
  pb1a6b00: "CA",        // California
  p339d5f70: "AK",       // Alaska
  p1377d680: "HI",       // Hawaii
};

/**
 * Reverse mapping: State code → Path ID
 * Auto-generated from pathToStateCode
 */
export const stateCodeToPath: Record<string, string> = Object.entries(
  pathToStateCode
).reduce((acc, [pathId, stateCode]) => {
  acc[stateCode] = pathId;
  return acc;
}, {} as Record<string, string>);

/**
 * All US state codes + DC + Federal for reference
 */
export const ALL_STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "FEDERAL"
] as const;

/**
 * Get state name from code
 */
export const stateNames: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  FEDERAL: "Federal FOIA",
};
